import { format, isFuture, isToday } from "date-fns";
import { nb } from "date-fns/locale";
import { DateSpan } from "features/VehicleSidebar/VehicleList/common/DateSpan";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { FC } from "react";
import { generatePath, useSearchParams } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { DropsBadge, HStack, Stack } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { ReserveAlternativeTransport } from "shared/types/alternativeTransport";
import { getPrintableFromVehicleType } from "shared/utils/alternativeTransport";
import { BACKUPBUS, RESERVEBUS } from "shared/utils/routes";
import { useTheme } from "styled-components";

type Props = {
  reserveAlternativeTransport: ReserveAlternativeTransport;
  elementSelected: boolean;
  isReserveVehicle: boolean;
  index: number;
};

export const ReserveOrBackupAlternativeTransportElement: FC<Props> = ({
  reserveAlternativeTransport,
  elementSelected,
  isReserveVehicle,
  index,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const {
    workShiftId,
    shiftStartTime,
    shiftEndTime,
    driverPhoneNumber,
    isAvailable,
    baseStopName,
    type,
    operatorName,
  } = reserveAlternativeTransport;

  const formattedShiftStartTime = shiftStartTime
    ? format(new Date(shiftStartTime), "HH:mm (dd.MM.yy)", { locale: nb })
    : "";

  const formattedShiftEndTime = shiftEndTime
    ? format(new Date(shiftEndTime), "HH:mm (dd.MM.yy)", { locale: nb })
    : "";

  const isNotToday = shiftStartTime ? !isToday(shiftStartTime) : false;

  const hasPhoneNumber = !!driverPhoneNumber && driverPhoneNumber.length > 0;

  const isFutureShift = shiftEndTime && isFuture(shiftEndTime);

  const isPast = !isFutureShift;

  const textColorMain = isPast
    ? theme.colorTextTertiary
    : theme.colorTextSecondary;

  const textColorSecondary = isPast
    ? theme.colorTextDisabled
    : theme.colorTextSecondary;

  /*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */
  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const path = isReserveVehicle ? RESERVEBUS : BACKUPBUS;
  const vehicleTitle = isReserveVehicle
    ? `Reserve ${index} ${workShiftId ?? ""}`
    : `Beredskap ${index} (${operatorName ?? ""})`;

  return (
    <ElementWrapper
      selected={elementSelected}
      backgroundColor={theme.colorBackgroundSecondary}
      color={textColorMain}
      isHover
      to={`${generatePath(path, {
        vehicleUuid: reserveAlternativeTransport.id,
      })}?${searchParams.toString()}`}
      linkState={{ vehicleTitle }}
    >
      <Stack
        gap="0.5"
        flex={1}
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <HStack height="25px" gap="6px">
          <Text bold style={{ color: textColorMain }}>
            {vehicleTitle}
          </Text>
          {!isAvailable && <DropsBadge semantics="info">I bruk</DropsBadge>}
        </HStack>

        <Text style={{ color: textColorSecondary }}>
          {`${baseStopName}, ${getPrintableFromVehicleType(type) ?? ""}`}
        </Text>
        <Text style={{ color: textColorMain }}>
          {formattedShiftStartTime}–{formattedShiftEndTime}
          {isNotToday && shiftStartTime && (
            <DateSpan>({format(shiftStartTime, "dd.MM.yy")})</DateSpan>
          )}
        </Text>
      </Stack>

      {hasPhoneNumber && (
        <CallButton phoneNumber={phoneNumberWithDigitsOnly!} size="xs" />
      )}
    </ElementWrapper>
  );
};
